import React, { useEffect, useRef, useState } from "react";
import { CaretUp, CheckCircle, WarningCircle, XCircle, Database, Pulse } from "@phosphor-icons/react";
import { fetchHealth } from "../lib/health";
import { relTime } from "../lib/incidents";

const STATUS_META = {
  healthy: { color: "#2A9D8F", label: "All systems online" },
  degraded: { color: "#FFB347", label: "Degraded" },
  down: { color: "#E63946", label: "Major outage" },
};

export default function SystemStatusFooter() {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const h = await fetchHealth();
        if (!stop) setHealth(h);
      } catch (e) {
        console.warn("SystemStatusFooter: health fetch failed", e);
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!health) return null;
  const meta = STATUS_META[health.status] || STATUS_META.degraded;

  return (
    <div ref={ref} className="fixed bottom-14 right-3 z-[1100]" data-testid="system-status">
      {open && (
        <div
          data-testid="system-status-panel"
          className="mb-2 w-[320px] sm:w-[360px] bg-tac-bg/95 backdrop-blur-xl border border-tac-border rounded-sm shadow-2xl text-xs overflow-hidden animate-fade-in"
        >
          <div className="px-3 py-2.5 border-b border-tac-border flex items-center gap-2">
            <Pulse size={14} weight="duotone" style={{ color: meta.color }} />
            <span className="font-bold uppercase tracking-[0.12em] text-[10px]" style={{ color: meta.color }}>
              {meta.label}
            </span>
            <span className="ml-auto text-[15px] font-mono-tac text-tac-faint tabular">
              {health.sources_online}/{health.sources_total} sources
            </span>
          </div>
          <div className="max-h-[60vh] overflow-y-auto tac-scroll">
            <Section title={`Dispatch Centers · ${health.dispatch_online}/${health.dispatch_total}`}>
              {health.dispatch_centers.map((d) => (
                <Row key={d.code} ok={d.ok} label={d.code} sub={d.name} latency={d.latency_ms} />
              ))}
            </Section>
            <Section title="Data Sources">
              {health.other_sources.map((s) => (
                <Row key={s.name} ok={s.ok} label={s.name} sub={s.status ? `HTTP ${s.status}` : "no response"} latency={s.latency_ms} />
              ))}
            </Section>
            <Section title="Infrastructure">
              <Row ok={health.database.ok} label="MongoDB" sub={health.database.error || "connected"} />
              <Row ok={!!health.last_refresh}
                   label="Background refresh"
                   sub={health.last_refresh ? `last sync ${relTime(health.last_refresh)}` : "no sync yet"} />
            </Section>
          </div>
          <div className="px-3 py-2 border-t border-tac-border text-[15px] font-mono-tac text-tac-faint">
            Checked {relTime(health.checked_at)} · refreshes every 60s
          </div>
        </div>
      )}

      <button
        data-testid="system-status-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2.5 py-1.5 bg-tac-bg/90 backdrop-blur-xl border border-tac-border hover:border-tac-borderBright rounded-sm shadow-lg transition-colors group"
        aria-label="System status"
      >
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}` }}
        />
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] hidden sm:inline" style={{ color: meta.color }}>
          {health.status === "healthy" ? "Online" : meta.label}
        </span>
        <span className="text-[15px] font-mono-tac tabular text-tac-dim group-hover:text-tac-text">
          {health.sources_online}/{health.sources_total}
        </span>
        <CaretUp size={10} weight="bold" className={`text-tac-faint transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-b border-tac-border last:border-b-0">
      <div className="px-3 py-1.5 bg-tac-surface/40 text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">
        {title}
      </div>
      <ul className="divide-y divide-tac-border/40">{children}</ul>
    </div>
  );
}

function Row({ ok, label, sub, latency }) {
  const Icon = ok ? CheckCircle : XCircle;
  const color = ok ? "#2A9D8F" : "#E63946";
  return (
    <li className="px-3 py-1.5 flex items-center gap-2.5">
      <Icon size={12} weight="fill" style={{ color }} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-tac-text text-[11px] font-medium leading-tight truncate">{label}</div>
        {sub && <div className="text-tac-faint text-[15px] font-mono-tac truncate">{sub}</div>}
      </div>
      {latency != null && (
        <div className="text-[15px] font-mono-tac tabular text-tac-faint shrink-0">
          {latency}ms
        </div>
      )}
    </li>
  );
}
